// The Keep card's figure rules: which of its three states the card is in,
// and what each row shows in each. Pure, so the rules are testable without
// the report; useKeepCard holds the state they read.
import { KeepCardValues, ReportValueCardProps } from "helpers/types";
import { NO_REPORT_DISPLAY } from "../reportFieldStates";

/**
 * What the Keep card's figures ARE right now.
 *
 * ONE state for the CARD rather than a flag per row, because every field of
 * that card is Bucket 1's by definition (the backend's _build_themed_cards
 * says so): they are produced together or not at all, and a run either
 * carried the bucket or it did not.
 *
 *   "unrun"   -- no completed run has produced a Keep card in this session.
 *                Every row shows its NO_REPORT seed, and whatever
 *                localStorage holds for it WAITS UNDERNEATH rather than being
 *                shown. That store is scoped to the client and accounts but
 *                has no period or ticker anywhere in its key
 *                (useReportStorage), so a figure read back out of it belongs
 *                to whichever of this client's reports was last on screen --
 *                another period's or another ticker's.
 *   "absent"  -- a run COMPLETED and carried no Bucket 1 data at all. That is
 *                an answer rather than a silence, so the rows say so:
 *                KEEP_NO_BUCKET_1 behind an N/A.
 *   "figures" -- a run produced the card. The rows carry the picked option's
 *                values.
 *
 * The two non-"figures" states are what task 3155 is: a run that carried no
 * Bucket 1 data left the whole card showing the PREVIOUS run's figures --
 * 60,000 shares, a $2,052,600 opening value, a 472.39% participation rate --
 * with no header picker beside them, because the picker only renders when
 * there is something to pick. Every one of those read as this run's result.
 */
export type KeepFiguresState = "unrun" | "absent" | "figures";

/**
 * The Keep card's own "no figure" sentence, and the one on these cards the
 * FRONTEND authors.
 *
 * Every other one is the backend's, single-homed in bucket_totals.py, because
 * every other one explains a figure the backend reached and refused. This one
 * explains a card the response never carried at all, which is a fact only the
 * reader of that response holds.
 *
 * It names the STOCK rather than the calculation because that is the question
 * the reader is actually asking. The card's header picker is absent in this
 * state -- there is nothing to pick -- so what they see is a figure with no
 * stock beside it and no way to find out which stock it is for, and asking
 * that is what sent this here (operator, task 3155).
 */
export const KEEP_NO_BUCKET_1 =
  "No figure — this run produced no Bucket 1 results, so there is no stock " +
  "for the Keep card to report on.";

/**
 * The Keep card's rows, named so that one of them can be marked as the
 * reader's own. Keys rather than labels: a label is display text, and
 * rewording one must not silently detach a row from its edit.
 */
export type KeepRowKey =
  | "shares_start"
  | "shares_end"
  | "value_start"
  | "value_end"
  | "dividends"
  | "participation_rate"
  | "basis"
  | "tax_savings";

export const NO_KEEP_ROWS_EDITED: ReadonlySet<KeepRowKey> =
  new Set<KeepRowKey>();

/**
 * What ONE Keep row shows, from the card's state and whether the reader has
 * taken that row over. Every row of the card reads through here, so the three
 * states are decided once rather than eight times.
 *
 * `stored` is the row's own persisted figure and `reason` the sentence the
 * backend sent about it. BOTH ARE READ ONLY IN THE "figures" STATE, and that
 * is the whole point of this function: in the other two the stored figure is
 * not this run's result, so showing it would credit it to a run that never
 * produced it, and a backend sentence about it would explain a number this
 * run never reached.
 *
 * An EDITED row outranks every state, because from the moment the reader
 * types a figure it is theirs: the row shows what they typed and carries no
 * reason, since an explanation of why the backend had none is describing a
 * number nobody is looking at any more. It is per ROW and never card-wide --
 * one shared flag would take the other seven rows out of their seed the
 * moment one was typed into, revealing seven stored figures nobody entered
 * this session as if this run had produced them.
 */
export const keepRowFigure = (
  state: KeepFiguresState,
  edited: boolean,
  stored: number | string,
  reason?: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> => {
  if (edited) return { value: stored };
  if (state === "figures") return { value: stored, unavailableReason: reason };
  // The empty sentinel WITH a reason is what MultiValuesCard renders as a
  // muted "N/A" behind the info icon, and what toPdfValues drops from the
  // client-facing export. Either half alone would be wrong here: the reason
  // without the sentinel would leave a figure underneath a row that reads
  // N/A, and the sentinel without the reason is the silent gap this state
  // exists to stop being.
  if (state === "absent")
    return { value: "", unavailableReason: KEEP_NO_BUCKET_1 };
  // No reason, on purpose. "No Report" is not a refusal -- nothing has run,
  // so there is nothing to explain -- and reportFieldStates keeps the two
  // apart deliberately: an N/A asserts the backend reached this figure and
  // could not produce it, which of a run nobody has made yet is false.
  return { value: NO_REPORT_DISPLAY };
};

/**
 * The Keep card's Participation Rate for ONE option of the card -- one stock,
 * or the Total -- as the stored figure and the sentence that qualifies it.
 * `keepRowFigure` above takes it from here and decides what the row does
 * with it.
 *
 * This is `timeWeightedReturnFigure`'s (summaryFigures) job for `card_4`
 * done for the Keep card's one refusable row, and the two hold the same contract because the
 * backend sends the same shape: `participation_rate` is null EXACTLY when
 * `participation_rate_unavailable` carries the reason (types.ts).
 *
 * The null becomes the EMPTY SENTINEL rather than travelling on, for the
 * three reasons that made card_4 do the same: the value is what gets STORED,
 * what the PDF prints and what survives the report's remount, so passing a
 * null through would leave the PREVIOUS option's rate underneath a row
 * reading N/A -- and on this card that is a rate credited to the wrong
 * ticker, which is the Keep card's own reported defect (task 3155).
 *
 * `?? ""` and not `|| ""`, on BOTH halves: a measured 0.00% is a result -- a
 * strategy that captured none of its stock's rise -- and a falsy test would
 * turn that result into a refusal. It is also what makes a payload from a job
 * that completed before this field shipped read exactly as it did before:
 * such a payload carries `participation_rate: 0` and no reason at all, so it
 * yields that 0 and no sentence.
 */
export const keepParticipationFigure = (
  values: Pick<
    KeepCardValues,
    "participation_rate" | "participation_rate_unavailable"
  >,
): {
  rate: number | string;
  reason?: string;
} => ({
  rate: values.participation_rate ?? "",
  reason: values.participation_rate_unavailable ?? undefined,
});
